import { Alert, Button, Card, CardContent, Checkbox, Chip, FormControlLabel, Grid, List, ListItem, ListItemText, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { ErrorState, LoadingState, PageHeader, StatusChip } from "../components";
import { formatDateTime } from "../i18n/format";
import help from "./HELP.md?raw";
import { useImpersonate, useTenant, useTenantActions, useTenantFlags, useTenantNotes, useTenantOp } from "./useData";

export function TenantDetail() {
  const { id = "" } = useParams();
  const tenant = useTenant(id);
  const notes = useTenantNotes(id);
  const actions = useTenantActions(id);
  const flags = useTenantFlags(id);
  const op = useTenantOp(id);
  const impersonate = useImpersonate();
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(14);
  const [note, setNote] = useState("");
  const [modules, setModules] = useState<string | null>(null);
  const [limits, setLimits] = useState<string | null>(null);
  const [flagKey, setFlagKey] = useState("");
  const [imp, setImp] = useState({ email: "", reason: "", minutes: 30, allow_writes: false });

  if (tenant.isLoading) return <LoadingState />;
  if (tenant.isError || !tenant.data) return <ErrorState message={tenant.error?.message ?? "Not found"} />;
  const t = tenant.data;
  const needReason = !reason.trim();
  return (
    <>
      <PageHeader help={help} title={t.name} subtitle={`${t.slug} · ${t.timezone} · ${t.base_currency}`} crumbs={[{ label: "Console" }, { label: "Organizations", to: "/console" }, { label: t.name }]}
        actions={<Stack direction="row" spacing={1}><StatusChip status={t.status} /><Chip label={`${t.subscription.plan ?? "no plan"} · ${t.subscription.status}`} size="small" /></Stack>} />
      {op.isError && <Alert severity="error" sx={{ mb: 2 }}>{op.error.message}</Alert>}
      {impersonate.isError && <Alert severity="error" sx={{ mb: 2 }}>{impersonate.error.message}</Alert>}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card><CardContent>
            <Typography variant="subtitle2" gutterBottom>Actions (reason required, all audited)</Typography>
            <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mb: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {["active", "suspended", "closed"].filter((s) => s !== t.status).map((s) => (
                <Button key={s} variant="outlined" disabled={needReason} onClick={() => op.mutate({ op: "status", body: { status: s, reason } })}>Set {s}</Button>
              ))}
              <TextField size="small" type="number" label="Days" value={days} onChange={(e) => setDays(Number(e.target.value))} sx={{ width: 100 }} />
              <Button variant="outlined" disabled={needReason} onClick={() => op.mutate({ op: "extend-trial", body: { days, reason } })}>Extend trial</Button>
            </Stack>
          </CardContent></Card>
          <Card sx={{ mt: 2 }}><CardContent>
            <Typography variant="subtitle2" gutterBottom>Enterprise overrides</Typography>
            <Typography variant="caption" color="text.secondary">Current modules: {t.subscription.modules.join(", ")}. Limits: {JSON.stringify(t.subscription.limits)}</Typography>
            <TextField label="Extra modules (comma separated spoke labels)" value={modules ?? ""} onChange={(e) => setModules(e.target.value)} sx={{ mt: 1 }} />
            <TextField label='Limits JSON, e.g. {"users": 100, "sites": 20}' value={limits ?? ""} onChange={(e) => setLimits(e.target.value)} sx={{ mt: 1 }} />
            <Button sx={{ mt: 1 }} variant="outlined" onClick={() => {
              let parsed: Record<string, number> | undefined;
              try { parsed = limits ? JSON.parse(limits) : undefined; } catch { return; }
              op.mutate({ op: "overrides", body: { modules: modules ? modules.split(/[\s,]+/).filter(Boolean) : undefined, limits: parsed, reason } });
            }}>Apply overrides</Button>
          </CardContent></Card>
          <Card sx={{ mt: 2 }}><CardContent>
            <Typography variant="subtitle2" gutterBottom>Feature flags</Typography>
            {Object.entries(flags.data?.flags ?? {}).map(([k, v]) => (
              <FormControlLabel key={k} label={`${k} ${flags.data?.declared[k] ? `— ${flags.data.declared[k]}` : ""}`}
                control={<Checkbox checked={v} onChange={(e) => op.mutate({ op: "flags", body: { key: k, enabled: e.target.checked, reason } })} />} />
            ))}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField size="small" label="Flag key" value={flagKey} onChange={(e) => setFlagKey(e.target.value)} />
              <Button disabled={!flagKey} onClick={() => { op.mutate({ op: "flags", body: { key: flagKey, enabled: true, reason } }); setFlagKey(""); }}>Enable</Button>
            </Stack>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderColor: "warning.main" }}><CardContent>
            <Typography variant="subtitle2" gutterBottom>Impersonate a user (support)</Typography>
            <Stack spacing={1}>
              <TextField size="small" label="User email" value={imp.email} onChange={(e) => setImp({ ...imp, email: e.target.value })} helperText={`Owners: ${t.owners.join(", ")}`} />
              <TextField size="small" label="Reason / ticket" value={imp.reason} onChange={(e) => setImp({ ...imp, reason: e.target.value })} />
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField size="small" type="number" label="Minutes" value={imp.minutes} onChange={(e) => setImp({ ...imp, minutes: Number(e.target.value) })} sx={{ width: 110 }} />
                <FormControlLabel control={<Checkbox checked={imp.allow_writes} onChange={(e) => setImp({ ...imp, allow_writes: e.target.checked })} />} label="Allow writes" />
                <Button variant="contained" color="warning" disabled={!imp.email || !imp.reason || impersonate.isPending} onClick={() => impersonate.mutate({ ...imp, organization: t.slug })}>Start</Button>
              </Stack>
            </Stack>
          </CardContent></Card>
          <Card sx={{ mt: 2 }}><CardContent>
            <Typography variant="subtitle2" gutterBottom>Notes</Typography>
            <Stack direction="row" spacing={1}><TextField size="small" fullWidth value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note" /><Button disabled={!note} onClick={() => { op.mutate({ op: "notes", body: { text: note } }); setNote(""); }}>Add</Button></Stack>
            <List dense>{notes.data?.map((n) => <ListItem key={n.id}><ListItemText primary={n.text} secondary={`${n.author} · ${formatDateTime(n.created_at)}`} /></ListItem>)}</List>
          </CardContent></Card>
          <Card sx={{ mt: 2 }}><CardContent>
            <Typography variant="subtitle2" gutterBottom>Audit trail</Typography>
            <List dense>{actions.data?.slice(0, 20).map((a) => <ListItem key={a.id}><ListItemText primary={`${a.action} — ${a.reason}`} secondary={`${a.actor} · ${formatDateTime(a.created_at)} · ${JSON.stringify(a.data)}`} /></ListItem>)}</List>
          </CardContent></Card>
        </Grid>
      </Grid>
    </>
  );
}
